import { memo, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { ModalBody, ModalContent, Modal, ModalHeader, ModalFooter } from '@heroui/react';
import { CalendarDate } from '@internationalized/date';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { type TermFreeRange, useCreateTermMutation } from '@/entities/Courses';
import { AppButton } from '@/shared/ui/AppButton';
import { CustomInput, CustomDatepicker, FieldHint } from '@/shared/ui/Fields';
import { scaleInOut } from '@/shared/lib/utils/modalTransitions';
import { useToast } from '@/shared/lib/hooks/useToast';
import {
  useTermDatePickerProps,
  useTermFormDates,
  testTermEndDate,
  testTermNumberFree,
  testTermStartDate,
  type TermDatesContext,
} from '../../../lib/termDates';
import styles from './AddTermModal.module.scss';

interface AddTermModalProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  intakeId: number;
  // Free periods of the intake by term number, from the terms list
  freeRanges?: TermFreeRange[];
  // Numbers of the terms that already have dates in this intake
  takenNumbers?: number[];
  onSuccess?: () => void;
}

const parseTermNumber = (title: string) => {
  const digits = title.match(/\d+/)?.[0];
  return digits ? Number(digits) : null;
};

interface TermFormData {
  title: string;
  startDate: CalendarDate | null;
  endDate: CalendarDate | null;
}

const termSchema = yup.object({
  title: yup
    .string()
    .required('Term title is required')
    .test('number-free', '', testTermNumberFree),
  startDate: yup
    .mixed()
    .required('Start date is required')
    .test('within-bounds', '', testTermStartDate),
  endDate: yup.mixed().required('End date is required').test('within-bounds', '', testTermEndDate),
}) as yup.ObjectSchema<TermFormData>;

export const AddTermModal = memo(
  ({ isOpen, onOpenChange, intakeId, freeRanges, takenNumbers, onSuccess }: AddTermModalProps) => {
    const { toast } = useToast();
    const [createTerm, { isLoading }] = useCreateTermMutation();
    // Without a number every free period of the intake is available, with a number — its period
    const [termNumber, setTermNumber] = useState<number | null>(null);
    const {
      ranges,
      blocked,
      hint: datesHint,
    } = useTermFormDates(termNumber, freeRanges, undefined, takenNumbers);

    const {
      control,
      handleSubmit,
      formState: { errors },
      reset,
      watch,
    } = useForm<TermFormData, TermDatesContext>({
      // @ts-ignore
      resolver: yupResolver(termSchema),
      context: { ranges, blockedMessage: blocked, takenNumbers: takenNumbers ?? [] },
      defaultValues: {
        title: '',
        startDate: null,
        endDate: null,
      },
    });
    const startDatePickerProps = useTermDatePickerProps(ranges);
    // The end stays in the period of the chosen start
    const endDatePickerProps = useTermDatePickerProps(ranges, watch('startDate'));

    const onSubmit = async (data: TermFormData) => {
      if (!data.startDate || !data.endDate) {
        return;
      }

      try {
        const startDateStr = `${data.startDate.year}-${String(data.startDate.month).padStart(2, '0')}-${String(data.startDate.day).padStart(2, '0')}`;
        const endDateStr = `${data.endDate.year}-${String(data.endDate.month).padStart(2, '0')}-${String(data.endDate.day).padStart(2, '0')}`;

        const result = await createTerm({
          intakeId,
          number: parseTermNumber(data.title) ?? 1,
          startDate: startDateStr,
          endDate: endDateStr,
        }).unwrap();

        toast({
          title: 'Success',
          description: result.message || 'Term created successfully',
          color: 'success',
        });

        reset();
        setTermNumber(null);
        onOpenChange(false);
        onSuccess?.();
      } catch (error: any) {
        toast({
          title: 'Error',
          description: error?.data?.message || 'Failed to create term',
          color: 'danger',
        });
      }
    };

    const handleClose = () => {
      reset({
        title: '',
        startDate: null,
        endDate: null,
      });
      setTermNumber(null);
      onOpenChange(false);
    };

    return (
      <Modal
        isOpen={isOpen}
        onOpenChange={handleClose}
        placement="center"
        motionProps={scaleInOut}
        size="2xl"
        classNames={{
          base: 'p-6 flex flex-col gap-5 max-w-[600px]',
          closeButton: styles.closeBtn,
        }}
      >
        <ModalContent>
          {() => (
            <form onSubmit={handleSubmit(onSubmit as any)}>
              <ModalHeader className={styles.title}>Add new term</ModalHeader>
              <ModalBody className="p-0 flex flex-col gap-6">
                <Controller
                  name="title"
                  control={control}
                  render={({ field }) => (
                    <CustomInput
                      {...field}
                      onChange={(event) => {
                        field.onChange(event);
                        setTermNumber(parseTermNumber(event.target.value));
                      }}
                      label="Term Number"
                      labelPlacement="outside"
                      placeholder="Enter term number"
                      errorMessage={errors.title?.message}
                      showError={!!errors.title}
                      isRequired
                    />
                  )}
                />

                <div className="flex flex-col gap-2">
                  <div className="flex gap-4">
                    <Controller
                      name="startDate"
                      control={control}
                      render={({ field }) => (
                        <CustomDatepicker
                          label="Start date"
                          labelPlacement="outside"
                          value={field.value}
                          onChange={field.onChange}
                          errorMessage={errors.startDate?.message}
                          showError={!!errors.startDate}
                          isRequired
                          {...startDatePickerProps}
                          placeholderValue={null}
                          className="flex-1"
                        />
                      )}
                    />

                    <Controller
                      name="endDate"
                      control={control}
                      render={({ field }) => (
                        <CustomDatepicker
                          label="End date"
                          labelPlacement="outside"
                          value={field.value}
                          onChange={field.onChange}
                          errorMessage={errors.endDate?.message}
                          showError={!!errors.endDate}
                          isRequired
                          {...endDatePickerProps}
                          placeholderValue={null}
                          className="flex-1"
                        />
                      )}
                    />
                  </div>
                  {/* The available dates apply to both pickers */}
                  <FieldHint>{datesHint}</FieldHint>
                </div>
              </ModalBody>
              <ModalFooter className="gap-4 pt-6 px-0">
                <AppButton fullWidth color="white" onPress={handleClose} type="button">
                  Cancel
                </AppButton>
                <AppButton
                  fullWidth
                  color="black"
                  type="submit"
                  isLoading={isLoading}
                  onClick={(e) => {
                    e.preventDefault();
                    handleSubmit(onSubmit as any)();
                  }}
                >
                  Create term
                </AppButton>
              </ModalFooter>
            </form>
          )}
        </ModalContent>
      </Modal>
    );
  },
);
